import React, { useEffect } from "react";
import Menu from "./Menu/Menu";
import { useTheme } from '../theming/default_theme';
import { useLocation, useNavigate } from "react-router-dom";
import { get } from "../api/apiService";
import Header from "./Header";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setMenuSelected } from "./Menu/MenuSlice";
import { apiEndPoints } from "../api/apiEndPoints";
import { convertDateFormat } from "../utils/dateUtils";
import StatusBadge from "./StatusBadge";
import blockUser from '../assets/icons/blockuser.png';
import user from '../assets/icons/user.png';
import referredHospital from '../assets/icons/referred-hospital.png';
import referredOn from '../assets/icons/referred-on.png';
import phone from '../assets/icons/phone.png';
import mapPin from '../assets/icons/map-pin.png';

export default function ReferralDetailsPage() {

  const location = useLocation();
  const referral = location.state?.referral;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const menuSelected = useAppSelector((state) => state.menu.menuSelected);
  const navigate = useNavigate();


  useEffect(() => {
    if (!referral) {
      navigate('/LandingPage', { replace: true });
    }
  }, [referral, navigate]);
  if (!referral) {
    return null;
  }


  const handleReferralClick = () => {

    console.log('Referral clicked');
    dispatch(setMenuSelected('Referrals'));
    navigate('/LandingPage');
  };

  return (
    <div className={` flex flex-row justify-start items-start h-screen w-screen`} >
      <Menu />
      <div className="flex flex-col justify-start items-start w-full h-full">
        <Header />
        {/* Main Content */}
        <div className="flex flex-col h-full w-full border border-2 mt-2" style={{ backgroundColor: theme.colors.background }}>
          {/* Breadcrumb */}
          <div className="flex items-center mb-6 text-gray-600">
            <div className="flex flex-row justify-start text-left items-left ml-5">
              <div className="cursor-pointer mr-2" onClick={() => handleReferralClick()}>Referrals</div><div className="mr-2">&gt;</div><div>Referral Details</div></div></div>
          {/* Patient Info Card */}
          <div className="mb-6 pl-10 pr-10">
            <div className="w-full bg-amber-50 rounded-lg shadow-sm border border-gray-100">
              <div className="flex flex-col p-4 w-full">
                {/* Profile section */}
                <div className="flex items-center justify-between mb-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4">
                    <img src={blockUser} alt="Referral" style={{ width: '50px', height: '50px' }} />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800">{referral.patientName}</h2>
                  <span className="ml-auto">
                    {referral.status === 'COMPLETED' || referral.status === 'DELIVERED' ? (
                      <StatusBadge backgroundColor="#E8F6EE" textColor="#27AE60" text="Delivered" />
                    ) : referral.status === 'REFERRED' ? (
                      <StatusBadge backgroundColor="#DBEAFE" textColor="#1D4ED8" text="Referred" />
                    ) : referral.status === 'INPROGRESS' ? (
                      <StatusBadge backgroundColor="#FFF8DC" textColor="#FFC107" text="In Progress" />
                    ) : (
                      <StatusBadge backgroundColor="#EEEEEE" textColor="#757575" text="Unknown" />
                    )}
                  </span>
                </div>
                <hr className="flex flex-row justify-center border-t-2 border-[#27AE60] w-[99%] pl-2 pr-2 mt-1" />
                {/* Information rows grouped into columns */}
                <div className="mt-5 flex flex-wrap justify-between gap-x-8 gap-y-4">
                  {/* Column 1: Age + Phone */}
                  <div className="flex flex-col min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <img src={user} alt="Age Icon" className="w-5 h-5 mr-2" />
                      <span className="text-sm font-semibold text-gray-700 mr-2">Age & Gender</span>
                      <span className="text-sm text-gray-600">{referral.age}, {referral.gender}</span>
                    </div>
                    <div className="flex items-center">
                      <img src={phone} alt="Phone Icon" className="w-5 h-5 mr-2" />
                      <span className="text-sm font-semibold text-gray-700 mr-2">Guardian Contact</span>
                      <span className="text-sm text-gray-600">{referral.patient.phone}</span>
                    </div>
                  </div>

                  {/* Column 2: Hospital + Location */}
                  <div className="flex flex-col min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <img src={referredHospital} alt="Hospital Icon" className="w-5 h-5 mr-2" />
                      <span className="text-sm font-semibold text-gray-700 mr-2">Referred Hospital</span>
                      <span className="text-sm text-gray-600">{referral.hospitalName}</span>
                    </div>
                    <div className="flex items-center">
                      <img src={mapPin} alt="Location Icon" className="w-5 h-5 mr-2" />
                      <span className="text-sm font-semibold text-gray-700 mr-2">Location</span>
                      <span className="text-sm text-gray-600">{referral.city}, {referral.state}</span>
                    </div>
                  </div>
                  {/* Column 3: Referred On */}
                  <div className="flex items-center min-w-[200px]">
                    <img src={referredOn} alt="Referred Icon" className="w-5 h-5 mr-2" />
                    <span className="text-sm font-semibold text-gray-700 mr-2">Referred On</span>
                    <span className="text-sm text-gray-600">{convertDateFormat(referral.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Past Referrals Table */}
          <div className="mt-0 pl-10 pr-10 min-w-0 overflow-hidden">
            <PastReferralsTable />
          </div>
        </div>
      </div>
    </div>
  );
}

export const PastReferralsTable = () => {
  const location = useLocation();
  const referral = location.state?.referral;
  const [pastReferrals, setPastReferrals] = React.useState<typeof referral>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    const fetchReferralData = async () => {
      setLoading(true);

      // Validate required data
      if (!referral?.patientId) {
        setError('Missing patient information');
        setLoading(false);
        return;
      }

      try {
        const url = `${apiEndPoints.referralPatientEndPoint}/${referral.patientId}`;
        const pastResponse = await get(url);

        if (!pastResponse || !Array.isArray(pastResponse)) {
          throw new Error('Invalid response format');
        }

        const currentReferralDate = new Date(referral.createdAt);

        const filteredPastReferrals = pastResponse
          .filter((ref: typeof referral) =>
            ref.id !== referral.id &&
            new Date(ref.createdAt) < currentReferralDate
          )
          .sort((a: typeof referral, b: typeof referral) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setPastReferrals(filteredPastReferrals);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch past referrals';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    // Only check for referral.patientId since that's what we need
    if (referral?.patientId) {
      fetchReferralData();
    } else {
      console.log('Skipping fetch - missing patientId:', referral?.patientId);
    }
  }, [referral]); // Only depend on referral object

  return (
    <div className="bg-amber-50 p-8 rounded-lg ">
      <h2 className="text-base font-medium text-gray-700 mb-4">Past Referrals</h2>

      <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-black-800">
        {pastReferrals.length === 0 ? (
          <div className="flex flex-row justify-center text-m text-gray-700 p-6">
            No past referrals
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Table Header */}
              <div className="grid grid-cols-3 bg-amber-50 border-b border-gray-200 sticky top-0 z-10 bg-[#F9F7ED">
                <div className="py-3 px-4 text-sm font-medium text-gray-800 bg-[#F9F7ED]">Hospital</div>
                <div className="py-3 px-4 text-sm font-medium text-gray-800 bg-[#F9F7ED] text-center">Requested On</div>
                <div className="py-3 px-4 text-sm font-medium text-gray-800 bg-[#F9F7ED] text-center">Status</div>
              </div>
              {/* Table Body */}
              <div className="divide-y divide-gray-200 max-h-[300px] overflow-y-auto">
                {Array.isArray(pastReferrals) && pastReferrals.map((referral: any, index: number) => (
                  <div key={index} className="grid grid-cols-3 hover:bg-gray-50 ">
                    <div className="py-4 px-4 text-sm">{referral.hospitalName}</div>
                    <div className="py-4 px-4 text-sm text-center">{convertDateFormat(referral.createdAt)}</div>
                    <div className="py-4 px-4 flex justify-center">
                      <span className="px-3 py-1 text-xs font-medium rounded-full">
                        {referral.status === 'COMPLETED' || referral.status === 'DELIVERED' ? (
                          <StatusBadge backgroundColor="#E8F6EE" textColor="#27AE60" text="Delivered" />
                        ) : referral.status === 'REFERRED' ? (
                          <StatusBadge backgroundColor="#DBEAFE" textColor="#1D4ED8" text="Referred" />
                        ) : referral.status === 'INPROGRESS' ? (
                          <StatusBadge backgroundColor="#FFF8DC" textColor="#FFC107" text="In Progress" />
                        ) : (
                          <StatusBadge backgroundColor="#EEEEEE" textColor="#757575" text="Unknown" />
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
